import useActions from '../../../hooks/service-actions.hook';
import useLoaders from '../../../hooks/use-loaders.hook';
import { useEntity } from '../../../hooks/use-entity.hook';
import { RecalculateScoreEffectActions } from '../../../common/services/recalculate-score/recalculate-score.service';
import { Loaders } from '../../../common/store/loaders/loaders.enum';
import { Flaggers } from '../../../common/store/flaggers/flaggers.enum';
import { FlaggersMethods } from '../../../common/store/flaggers/flaggers.methods';

interface ModalConfig {
  title: string;
  message: string;
  actionKey: string;
}

interface ModalData {
  show: boolean;
  config: ModalConfig | null;
}

interface ActionMap {
  [key: string]: () => void;
}

const useRecalculateScoresOptions = () => {
  const { recalculateServicePackageScore, recalculateGovResScore } = useActions(RecalculateScoreEffectActions);
  const { isLoading } = useLoaders({ loaders: [Loaders.RecalculateServicePackageScoreModalFlagger, Loaders.RecalculateGovResScoreModalFlagger] })

  const { entityData: servicePackageModalData, setEntityData: setServicePackageModalData } = useEntity({
    entityType: Flaggers.RecalculateServicePackageScoreModalFlagger
  });

  const { entityData: govResModalData, setEntityData: setGovResModalData } = useEntity({
    entityType: Flaggers.RecalculateGovResScoreModalFlagger
  });

  const servicePackageModalState = servicePackageModalData as ModalData || { show: false, config: null };
  const govResModalState = govResModalData as ModalData || { show: false, config: null };

  const activeModalState = servicePackageModalState.show ? servicePackageModalState : govResModalState;

  const actionMap: ActionMap = {
    'service-package': recalculateServicePackageScore,
    'gov-res': recalculateGovResScore
  };

  const handleServicePackageRecalculate = () => {
    setServicePackageModalData({
      show: true,
      config: {
        title: "Recalculate Service & Package Score",
        message: "Are you sure you want to recalculate the score for all services and packages? This action cannot be undone.",
        actionKey: 'service-package'
      }
    }, FlaggersMethods.UpdateFlagger);
  };

  const handleGovResRecalculate = () => {
    setGovResModalData({
      show: true,
      config: {
        title: "Recalculate G&R Score", 
        message: "Are you sure you want to recalculate the score for all G&R? This action cannot be undone.",
        actionKey: 'gov-res'
      }
    }, FlaggersMethods.UpdateFlagger);
  };

  const handleServicePackageModalCancel = () => {
    setServicePackageModalData({
      show: false,
      config: null
    }, FlaggersMethods.ResetFlagger);
  };

  const handleGovResModalCancel = () => {
    setGovResModalData({
      show: false,
      config: null
    }, FlaggersMethods.ResetFlagger);
  };


  const handleModalCancel = () => {
    handleServicePackageModalCancel();
    handleGovResModalCancel();
  };

  const handleModalConfirm = () => {
    const actionKey = activeModalState?.config?.actionKey;
    if (actionKey && actionMap[actionKey]) {
      actionMap[actionKey]();
    }
  };

  const RecalculateScoresOptions = [
    {
      title: "Recalculate Service & Package Score",
      description: "Recalculate the score for all the services and packages",
      type: 'button',
      buttonText: "Recalculate",
      onClick: handleServicePackageRecalculate,
      modalCancel: handleServicePackageModalCancel
    },{
      title: "Recalculate G&R Score",
      description: "Recalculate the score for all the G&R",
      type: 'button',
      buttonText: "Recalculate",
      onClick: handleGovResRecalculate,
      modalCancel: handleGovResModalCancel
    }
  ]

  return {
    options: RecalculateScoresOptions,
    isLoading,
    modalState: {
      activeModalState,
      handleModalCancel,
      handleModalConfirm
    },
    actionMap
  };
}

export default useRecalculateScoresOptions;
